import React, { useEffect, useRef, useState } from 'react';
import { ConnectionAccessRefused, readTenantConnections, revokeTenantConnection, type TenantConnectionPage } from '../../lib/tenant-connections.js';

/** Metadata only: provider credentials never enter the conversation or this panel. */
export function TenantConnectionsPanel({ chatSessionId }: { chatSessionId: string }): React.ReactElement {
  const [snapshot, setSnapshot] = useState<{ key: string; page: TenantConnectionPage } | null>(null);
  const [notice, setNotice] = useState(''), [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);
  const source = useRef<AbortController | null>(null), generation = useRef(0);
  const cursor = useRef<string | null>(null);
  const load = async (after: string | null): Promise<void> => {
    const signal = source.current?.signal; if (!signal || signal.aborted) return;
    const seq = ++generation.current; setBusy(true); setConfirm(null);
    // Clear stale metadata rather than presenting it as current connection authority.
    setSnapshot(null);
    try {
      const page = await readTenantConnections(chatSessionId, after, signal);
      if (signal.aborted || generation.current !== seq) return;
      cursor.current = after; setSnapshot({ key: chatSessionId, page }); setNotice('');
    } catch (error) {
      if (!signal.aborted && generation.current === seq) setNotice(error instanceof ConnectionAccessRefused
        ? 'Connection access is no longer available.' : 'Connections could not be refreshed. Try again.');
    } finally { if (!signal.aborted && generation.current === seq) setBusy(false); }
  };
  useEffect(() => {
    const controller = new AbortController(); source.current = controller; cursor.current = null;
    setSnapshot(null); setConfirm(null); setNotice(''); void load(null);
    return () => { controller.abort(); ++generation.current; };
  }, [chatSessionId]);
  const revoke = async (id: string, version: number): Promise<void> => {
    const signal = source.current?.signal; if (!signal || signal.aborted || busy) return;
    const seq = ++generation.current; setBusy(true); setConfirm(null);
    try {
      await revokeTenantConnection(chatSessionId, id, version, signal);
      if (!signal.aborted && generation.current === seq) await load(cursor.current);
    } catch (error) {
      if (!signal.aborted && generation.current === seq) {
        setSnapshot(null); setNotice(error instanceof ConnectionAccessRefused ? 'Connection access is no longer available.'
          : 'Disconnect was not confirmed. Refresh connections before trying again.');
      }
    } finally { if (!signal.aborted && generation.current === seq) setBusy(false); }
  };
  const page = snapshot?.key === chatSessionId ? snapshot.page : null;
  return <details><summary>Connected provider accounts</summary>
    <p>These connections belong to your project. Disconnect stops future tool access here; revoke the token at the provider to invalidate it there.</p>
    {notice && <p role="status">{notice}</p>}
    {busy && <p role="status">Updating connections…</p>}
    {page && !page.connections.length && <p>No provider connections on this page.</p>}
    {page?.connections.map(row => <div key={row.id}>
      <p>{row.provider} · {row.accountId} · {row.status}</p>
      <p>Expires {new Date(row.expiresAt).toLocaleString()} · {row.resources.map(resource => `${resource.id} (${resource.environment})`).join(', ')}</p>
      {!row.revocable && <p>This connection is shared with another project. Manage it through the account owner.</p>}
      {row.revocable && row.status !== 'revoked' && (confirm === row.id ? <>
        <p>Disconnect {row.provider} account {row.accountId} from tool access?</p>
        <button type="button" disabled={busy} onClick={() => { void revoke(row.id, row.version); }}>Confirm disconnect</button>
        <button type="button" disabled={busy} onClick={() => setConfirm(null)}>Keep connected</button>
      </> : <button type="button" disabled={busy} onClick={() => setConfirm(row.id)}>Disconnect {row.provider}</button>)}
    </div>)}
    <nav aria-label="Connection pages">
      <button type="button" disabled={busy} onClick={() => { void load(null); }}>Refresh connections</button>
      {page?.nextAfter && <button type="button" disabled={busy} onClick={() => { void load(page.nextAfter); }}>More connections</button>}
    </nav>
  </details>;
}
